"use client";

import React from "react";
import { motion } from "framer-motion";
import { Wrench, ShieldCheck, Zap, Flame, CircleCheck as CheckCircle2, Clock, MapPin, PhoneCall, MessageCircle, Award, Truck, Sparkles } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { PHONE_LOCAL, PHONE_TEL_HREF, WHATSAPP_HREF } from "@/lib/contact";
const technicalServices = [{
  icon: Flame,
  title: "تشخيص ومعالجة أعطال أفران أريستون",
  description: "فحص دقيق لكافة موديلات أريستون الغاز والكهرباء والبلت إن، ومعالجة ضعف الشعلات، انسداد الفواني، وتذبذب الحرارة."
}, {
  icon: ShieldCheck,
  title: "قطع غيار أريستون الأصلية 100%",
  description: "توفير واستبدال الشمعات، مفاتيح التحكم، موصلات الإشعال، ومقابض الأفران بقطع غيار أصلية مطابقة لمواصفات المصنع الإيطالي."
}, {
  icon: Zap,
  title: "إصلاح نظام الإشعال الذاتي والشواية",
  description: "معالجة مشاكل توقف القدّاحة الكهربائية، تكرار طقطقة الإشعال دون اشتعال، وصيانة دينامو الشواية الدوارة والمفاتيح."
}, {
  icon: Wrench,
  title: "معايرة الثرموستات وحساسات الأمان",
  description: "ضبط حساس الأمان الحراري (الثرموكبل) لمنع انطفاء النار المفاجئ، ومعايرة منظم الحرارة لضمان تسوية متكافئة لجميع المخبوزات."
}];
const reasonsToChoose = [{
  icon: Award,
  title: "فنيون معتمدون للأجهزة الإيطالية",
  description: "فريق مدرب باحترافية على تقنيات أفران أريستون المعقدة وأنظمة الإشعال والتحكم الرقمي الحديثة."
}, {
  icon: Truck,
  title: "زيارة منزلية فورية داخل الرياض",
  description: "خدمة تصليح فورية في منزلك دون الحاجة لنقل الفرن، مع توفير كافة معدات الفحص وقطع الغيار في نفس الزيارة."
}, {
  icon: Sparkles,
  title: "نظافة وسرعة في إنجاز العمل",
  description: "الحفاظ التام على نظافة المطبخ ومكان العمل أثناء الفك والتركيب، مع إجراء اختبارات أمان شاملة قبل مغادرة الفني."
}, {
  icon: CheckCircle2,
  title: "ضمان شامل ومعتمد",
  description: "نقدم ضماناً كتابياً معتمداً على جميع قطع الغيار المستبدلة وعلى جودة ودقة عمل الفني لراحة بالك."
}];
const riyadhAreas = ["شمال الرياض (الملقا، الياسمين، العقيق، النخيل، حطين)", "شرق الرياض (اليرموك، الرمال، الحمراء، قرطبة، الروضة)", "وسط الرياض (المروج، المصيف، العليا، السليمانية)", "غرب وجنوب الرياض (لبن، طويق، الشفاء، السويدي)"];
const faqItems = [{
  id: "item-1",
  question: "كم تستغرق زيارة صيانة فرن أريستون بالمنزل؟",
  answer: "تستغرق معظم عمليات الصيانة والفحص المنزلي لأفران أريستون بين 45 إلى 90 دقيقة بحسب نوع العطل والحاجة لتركيب قطع غيار، حيث يتم الفحص والإصلاح الفوري في مكان الفرن دون الحاجة لنقله."
}, {
  id: "item-2",
  question: "هل قطع الغيار المستخدمة لأفران أريستون أصلية؟",
  answer: "نعم، نحرص في لمسة احتراف على توفير قطع غيار أريستون الأصلية المعتمدة (حساسات، مفاتيح، زجاج حراري، شمعات، ومحابس أمان) لضمان كفاءة الفرن وسلامة أفراد الأسرة."
}, {
  id: "item-3",
  question: "ما هي شروط وضمان خدمة الصيانة؟",
  answer: "يحصل العميل على سند ضمان معتمد يشمل قطع الغيار المركبة وأجور يد الفني. في حال تكرار العطل خلال فترة الضمان يتم الفحص مجاناً وبأولوية قصوى."
}, {
  id: "item-4",
  question: "كيف يمكنني حجز موعد لصيانة الفرن في الرياض؟",
  answer: `يمكنك الحجز بسهولة عبر الاتصال المباشر على ${PHONE_LOCAL} أو إرسال رسالة واتساب توضح نوع الفرن (غاز/كهرباء) وطبيعة العطل لتحديد أقرب موعد زيارة في نفس اليوم.`
}];
export default function AristonMaintenance() {
  return <section id="aristonovenmaintenanceservice" data-nav="light" dir="rtl" className="relative bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-6xl">
        
        {/* Block 1: Service Overview & Technical Diagnosis */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="mb-16 md:mb-20">
          <div className="text-center max-w-3xl mx-auto mb-10 md:mb-12">
            <Badge variant="outline" className="mb-3 px-3 py-1 border-primary/30 text-primary">
              صيانة متخصصة في الرياض
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground mb-4">
              صيانة أفران أريستون بالرياض بأعلى دقة واحترافية
            </h2>
            <p className="text-muted-foreground text-base md:text-lg leading-relaxed">
              نقدم في لمسة احتراف حلولاً هندسية متكاملة لصيانة أفران أريستون الإيطالية (غاز وكهرباء) في منزلك بالرياض، مع فحص أنظمة الأمان والإشعال وتوفير قطع الغيار الأصلية.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {technicalServices.map((item, i) => {
            const Icon = item.icon;
            return <motion.div key={item.title} data-index={i} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.08
            }}>
                  <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow">
                    <CardHeader className="flex flex-row items-start gap-4">
                      <div className="p-3 rounded-lg bg-primary/10 text-primary shrink-0">
                        <Icon className="size-6" />
                      </div>
                      <div>
                        <CardTitle className="text-lg font-semibold text-foreground mb-2">
                          {item.title}
                        </CardTitle>
                        <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                          {item.description}
                        </CardDescription>
                      </div>
                    </CardHeader>
                  </Card>
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* Block 2: Why Choose لمسة احتراف */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="mb-16 md:mb-20">
          <div className="border-t border-border pt-12 md:pt-16 mb-10 text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
              لماذا تختار لمسة احتراف لصيانة فرن أريستون؟
            </h3>
            <p className="text-muted-foreground max-w-2xl mx-auto text-base">
              نلتزم بتقديم خدمة موثوقة تحفظ أداء جهازك وتوفر لك ولعائلتك أعلى معايير السلامة والأمان.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {reasonsToChoose.map((feat, i) => {
            const FeatIcon = feat.icon;
            return <motion.div key={feat.title} data-index={i} initial={{
              opacity: 0,
              y: 14
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.08
            }}>
                  <Card className="h-full border border-border bg-card text-card-foreground shadow-xs">
                    <CardHeader className="p-5">
                      <div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center text-primary mb-3">
                        <FeatIcon className="size-6" />
                      </div>
                      <CardTitle className="text-base font-semibold mb-2">
                        {feat.title}
                      </CardTitle>
                      <CardDescription className="text-xs md:text-sm text-muted-foreground leading-relaxed">
                        {feat.description}
                      </CardDescription>
                    </CardHeader>
                  </Card>
                </motion.div>;
          })}
          </div>
        </motion.div>

        {/* Block 3: Service Coverage in Riyadh */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="mb-16 md:mb-20">
          <div className="rounded-2xl border border-border bg-muted/60 p-6 md:p-8">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
              <div className="space-y-2 max-w-xl">
                <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                  <MapPin className="size-4" />
                  <span>تغطية شاملة لجميع أحياء الرياض</span>
                </div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground">
                  خدمة منزلية متكاملة أينما كنت في العاصمة
                </h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  تصلك سيارات الصيانة المتنقلة والمجهزة بجميع قطع أريستون وأدوات القياس لفحص وإصلاح الفرن داخل مطبخك فوراً.
                </p>
              </div>
              <div className="w-full md:w-auto grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {riyadhAreas.map((area, i) => <div key={i} data-index={i} className="flex items-center gap-2 p-2.5 rounded-lg bg-background border border-border text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span className="font-medium text-xs md:text-sm">{area}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Block 4: FAQ Accordion */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }} className="mb-16 md:mb-20 max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
              الأسئلة الشائعة حول صيانة أريستون
            </h3>
            <p className="text-muted-foreground text-sm">
              إجابات واضحة عن مدة الزيارة، أسعار قطع الغيار، وطريقة الحجز.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full border rounded-xl divide-y border-border bg-card">
            {faqItems.map((faq, i) => <AccordionItem key={faq.id} value={faq.id} data-index={i} className="px-4">
                <AccordionTrigger className="text-sm md:text-base font-semibold hover:no-underline text-right py-4">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-sm leading-relaxed pb-4">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </motion.div>

        {/* Block 5: CTA Panel */}
        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        ease: "easeOut"
      }}>
          <div className="rounded-2xl bg-secondary text-secondary-foreground p-8 md:p-12 text-center max-w-4xl mx-auto shadow-lg relative overflow-hidden">
            <h3 className="text-2xl md:text-3xl font-bold mb-3">
              هل تواجه عطلاً في فرن أريستون اليوم؟
            </h3>
            <p className="text-secondary-foreground/80 max-w-xl mx-auto mb-8 text-sm md:text-base leading-relaxed">
              فريقنا جاهز لمساعدتك فوراً في أي حي داخل الرياض. تواصل معنا هاتفياً أو عبر الواتساب لتحديد موعد الزيارة المنزلية السريعة.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button asChild size="lg" className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90 text-base font-medium px-8 shadow-sm">
                <Link to={WHATSAPP_HREF} newTab className="inline-flex items-center gap-2">
                  <MessageCircle className="size-5" />
                  <span>حجز عبر واتساب</span>
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto border-secondary-foreground/20 bg-secondary text-secondary-foreground hover:bg-secondary-foreground/10 text-base font-medium px-8">
                <a href={PHONE_TEL_HREF} className="inline-flex items-center gap-2">
                  <PhoneCall className="size-5" />
                  <span>اتصال مباشر: {PHONE_LOCAL}</span>
                </a>
              </Button>
            </div>
          </div>
        </motion.div>

      </div>
    </section>;
}